import React, { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { api, formatApiError } from "../../pages/Admin";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import EnquiryRow from "./EnquiryRow";
import ConfirmDelete from "./ConfirmDelete";

export default function AdminDashboard({ onUnauthorized }) {
  const { toast } = useToast();
  const fail = (err) => toast({ title: "Error", description: formatApiError(err.response?.data?.detail), variant: "destructive" });
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [pending, setPending] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/contact");
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else fail(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const doDelete = async () => {
    const ids = pending;
    setPending([]);
    try {
      const { data } = await api.post("/contact/delete", { ids });
      toast({ title: `Deleted ${data.deleted} ${data.deleted === 1 ? "enquiry" : "enquiries"}` });
      setSelected((s) => s.filter((x) => !ids.includes(x)));
      load();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <main className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-dashboard">
      <h1 className="text-3xl font-semibold tracking-tight">Contact form enquiries</h1>
      <p className="mt-2 text-slate-600" data-testid="admin-enquiry-count">
        {rows.length} {rows.length === 1 ? "enquiry" : "enquiries"}
      </p>
      <div className="mt-6 rounded-3xl border border-slate-200 bg-white overflow-hidden">
        {rows.length === 0 ? (
          <p className="p-10 text-center text-slate-500" data-testid="admin-empty">No enquiries yet.</p>
        ) : (
          <>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 bg-slate-50/60">
              <Checkbox checked={allSelected} data-testid="admin-select-all"
                onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])} />
              <span className="text-sm text-slate-600 flex-1">
                {selected.length ? `${selected.length} selected` : "Select all"}
              </span>
              {selected.length > 0 && (
                <Button size="sm" onClick={() => setPending(selected)} data-testid="admin-delete-selected-btn"
                  className="rounded-full bg-red-600 hover:bg-red-700 text-white h-8">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete selected
                </Button>
              )}
            </div>
            <ul className="divide-y divide-slate-100" data-testid="admin-enquiry-list">
              {rows.map((r) => (
                <EnquiryRow key={r.id} r={r} selected={selected.includes(r.id)}
                  onToggle={() => toggle(r.id)} onDelete={() => setPending([r.id])} />
              ))}
            </ul>
          </>
        )}
      </div>
      <ConfirmDelete count={pending.length} onCancel={() => setPending([])} onConfirm={doDelete} />
    </main>
  );
}
